import React from 'react';

// Defining all interfaces inline using lowercase naming convention
interface tabsProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}

interface tabsListProps {
  className?: string;
  children: React.ReactNode;
  activeValue?: string;
  onTabChange?: (value: string) => void;
}

interface tabsTriggerProps {
  value: string;
  className?: string;
  children: React.ReactNode;
  activeValue?: string;
  onTabChange?: (value: string) => void;
}

interface tabsContentProps {
  value: string;
  className?: string;
  children: React.ReactNode;
  activeValue?: string;
}

// Using React Context for better prop management
const TabsContext = React.createContext<{
  activeValue?: string;
  onTabChange?: (value: string) => void;
}>({});

/**
 * Tabs - A compound component for creating accessible tabbed interfaces
 *
 * @component
 * @example
 * ```tsx
 * <Tabs defaultValue="tab1">
 *   <TabsList>
 *     <TabsTrigger value="tab1">First Tab</TabsTrigger>
 *     <TabsTrigger value="tab2">Second Tab</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="tab1">First tab content</TabsContent>
 *   <TabsContent value="tab2">Second tab content</TabsContent>
 * </Tabs>
 * ```
 */
export const Tabs: React.FC<tabsProps> = ({ value, defaultValue, onValueChange, className = '', children }) => {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const activeValue = value !== undefined ? value : internalValue;

  const handleTabChange = React.useCallback(
    (newValue: string) => {
      if (value === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [value, onValueChange],
  );

  // Using Context to pass values down to all child components
  const contextValue = React.useMemo(
    () => ({
      activeValue,
      onTabChange: handleTabChange,
    }),
    [activeValue, handleTabChange],
  );

  return (
    <TabsContext.Provider value={contextValue}>
      <div className={`tabs ${className}`} role="tablist">
        {children}
      </div>
    </TabsContext.Provider>
  );
};

/**
 * TabsList - Container for tab triggers that provides proper styling and layout
 *
 * @component
 * @example
 * ```tsx
 * <TabsList>
 *   <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 *   <TabsTrigger value="tab2">Tab 2</TabsTrigger>
 * </TabsList>
 * ```
 */
export const TabsList: React.FC<tabsListProps> = ({ className = '', children }) => {
  return (
    <div className={`relative flex space-x-1 border-b border-gray-200 dark:border-gray-800 ${className}`}>
      {children}
    </div>
  );
};

/**
 * TabsTrigger - Interactive button that controls tab selection
 *
 * @component
 * @example
 * ```tsx
 * <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 * ```
 */
export const TabsTrigger: React.FC<tabsTriggerProps> = ({ value, className = '', children }) => {
  const { activeValue, onTabChange } = React.useContext(TabsContext);
  const isActive = value === activeValue;

  return (
    <button
      className={`relative px-4 py-2.5 text-sm font-medium transition-all duration-200 rounded-t-lg
        ${
          isActive
            ? 'text-primary bg-primary/5 dark:bg-primary/10'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
        }
        ${className}`}
      onClick={() => onTabChange?.(value)}
      role="tab"
      aria-selected={isActive}
      id={`tab-${value}`}
      aria-controls={`tabpanel-${value}`}
    >
      {children}
      {isActive && (
        <div
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
          style={{
            transform: 'translateY(1px)',
            boxShadow: '0 0 8px rgba(var(--primary-rgb), 0.4)',
          }}
        />
      )}
    </button>
  );
};

/**
 * TabsContent - Container for tab panel content that shows/hides based on tab selection
 *
 * @component
 * @example
 * ```tsx
 * <TabsContent value="tab1">Content for first tab</TabsContent>
 * ```
 */
export const TabsContent: React.FC<tabsContentProps> = ({ value, className = '', children }) => {
  const { activeValue } = React.useContext(TabsContext);

  if (value !== activeValue) return null;

  return (
    <div className={`pt-4 ${className}`} role="tabpanel" id={`tabpanel-${value}`} aria-labelledby={`tab-${value}`}>
      {children}
    </div>
  );
};
